import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { shortLabel } from "@/lib/scoring";

const get = (r, k) => k.split(".").reduce((o, p) => (o == null ? o : o[p]), r);

export function statColumns(outcomes, discipline, rangeLabel, showPct) {
  const cols = [
    ["session_total", "Session Total", "Most recent session in the selected dates"], ["session_avg", "Session Avg", "Most recent session in the selected dates"],
    ["total", `Total · ${rangeLabel}`], ["avg", `Avg · ${rangeLabel}`],
    ["cycle_total", "Cycle Total", "Current 4-week program cycle"], ["cycle_avg", "Cycle Avg", "Current 4-week program cycle"],
    ["life_total", "Career Total", "All completed sessions"], ["life_avg", "Career Avg", "All completed sessions"],
    ["sessions", "Sessions"], ["attempts", discipline === "pitching" ? "Pitches" : "Swings"],
  ];
  if (discipline !== "pitching") cols.push(["contact_pct", "Contact %", "Swings that weren't strikeouts ÷ all scored swings", "%"], ["hard_contact_pct", "Hard-Contact %", "(Hard GB + Line Drive + XBH) ÷ all scored swings", "%"]);
  outcomes.forEach((o) => { cols.push([`counts.${o.key}`, shortLabel(o), o.label]); if (showPct) cols.push([`pct.${o.key}`, `${shortLabel(o)} %`, `${o.label} % of attempts`, "%"]); });
  if (discipline === "pitching") cols.push(["strikes", "Total Strikes"], ["strike_pct", "Strike %", "All strikes ÷ pitches", "%"], ["located_strike_pct", "Target-Location %", "Strikes to the intended location ÷ pitches", "%"], ["ball_pct", "Ball %", "", "%"]);
  return cols;
}

// Excel-style sortable leaderboard: one row per player, sticky name column, every stat column sortable both ways.
export default function StatsTable({ rows, outcomes, discipline, rangeLabel, onPlayer, extra, idp = "stats" }) {
  const [sort, setSort] = useState({ key: "rank", dir: 1 });
  const [showPct, setShowPct] = useState(false);
  const cols = statColumns(outcomes, discipline, rangeLabel, showPct);
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const x = get(a, sort.key), y = get(b, sort.key);
    if (sort.key === "rank") return ((x ?? 1e9) - (y ?? 1e9)) * sort.dir || a.name.localeCompare(b.name);
    if (sort.key === "name") return a.name.localeCompare(b.name) * sort.dir;
    return ((y ?? 0) - (x ?? 0)) * sort.dir || a.name.localeCompare(b.name);
  }), [rows, sort]);
  const click = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const Head = ({ k, label, title, cls = "" }) => (
    <th title={title} className={`px-3 py-3 font-bold whitespace-nowrap ${cls}`}>
      <button data-testid={`${idp}-sort-${k.replace(".", "-")}`} onClick={() => click(k)} className={`inline-flex items-center gap-1 uppercase tracking-wider text-[11px] transition-colors ${sort.key === k ? "text-[#2563EB]" : "text-slate-500 hover:text-[#0B0F19]"}`}>
        {label}{sort.key === k && (sort.dir === 1 ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
      </button>
    </th>
  );
  return (
    <div data-testid={`${idp}-table`}>
      <label className="mb-2 inline-flex items-center gap-2 text-xs font-semibold text-slate-500"><input data-testid={`${idp}-show-pct`} type="checkbox" checked={showPct} onChange={(e) => setShowPct(e.target.checked)} className="accent-[#2563EB]" /> Show result percentages</label>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Head k="rank" label="#" cls="sticky left-0 z-20 bg-slate-50 w-12 min-w-[48px] shadow-[1px_0_0_#F8FAFC]" />
              <Head k="name" label="Player" cls="sticky left-12 z-20 bg-slate-50 text-left min-w-[150px] border-r border-slate-200" />
              {cols.map(([k, l, t]) => <Head key={k} k={k} label={l} title={t} cls="text-right" />)}
              {extra && <th className="px-3 py-3 text-[11px] uppercase tracking-wider text-slate-500 text-left">{extra.label}</th>}
            </tr>
          </thead>
          <tbody>
            {!sorted.length && <tr><td colSpan={cols.length + 3} data-testid={`${idp}-empty`} className="px-4 py-8 text-center text-sm text-slate-500">No completed sessions match these filters yet.</td></tr>}
            {sorted.map((r, i) => (
              <tr key={r.id || `${r.name}-${i}`} data-testid={`${idp}-row-${r.id || i}`} className="group border-b border-slate-100 last:border-0 hover:bg-[#2563EB]/[0.04]">
                <td className="sticky left-0 z-10 bg-white group-hover:bg-[#F6F8FE] w-12 min-w-[48px] px-3 py-3 font-extrabold text-slate-400 shadow-[1px_0_0_#fff]">{r.rank ?? "—"}</td>
                <td className="sticky left-12 z-10 bg-white group-hover:bg-[#F6F8FE] px-3 py-3 border-r border-slate-100">
                  {onPlayer ? <button data-testid={`${idp}-player-${r.id}`} onClick={() => onPlayer(r)} className="text-left font-bold text-[#0B0F19] hover:text-[#2563EB] underline-offset-2 hover:underline">{r.name}</button> : <span className="font-bold text-[#0B0F19]">{r.name}</span>}
                  <span className="block text-[11px] font-medium text-slate-400">{r.age_group || "—"}{r.rank == null ? ` · unranked (needs more ${discipline === "pitching" ? "pitches" : "swings"})` : ""}</span>
                </td>
                {cols.map(([k, , , unit]) => <td key={k} className={`px-3 py-3 text-right tabular-nums ${k === "avg" ? "font-extrabold text-[#2563EB]" : "text-[#0B0F19]"}`}>{get(r, k) === null ? "—" : `${get(r, k) ?? 0}${unit || ""}`}</td>)}
                {extra && <td className="px-3 py-3 min-w-[200px]">{extra.render(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
